import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, EyeIcon, PencilSquareIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import MarkdownView from '../components/MarkdownView';
import KBLayout from '../components/KBLayout';
import { kbApi, KBStatus } from '../services/kbApi';
import { useAuthStore } from '../stores/authStore';

/**
 * Create / edit a KB article. Single page for both — if `:id` is present
 * we hydrate from the API and PUT on save; otherwise we POST a new one.
 *
 * The editor is intentionally minimal: textarea + live preview pane,
 * markdown only, no rich-text toolbar. Bundle stays small, auditing is
 * trivial, and authors can copy/paste examples from any markdown source.
 */
const KBArticleEditPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  // Existing categories/subcategories for the <datalist> suggestions, so
  // authors reuse names instead of creating near-duplicates.
  const [taxonomy, setTaxonomy] = useState<{ category: string; subcategories: { name: string }[] }[]>([]);
  const [tagsInput, setTagsInput] = useState('');
  const [status, setStatus] = useState<KBStatus>('draft');
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [uploadingImage, setUploadingImage] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Insert markdown at the current cursor position (or append at the end
  // when the textarea isn't focused). Keeps the caret right after the
  // inserted snippet so the author can keep typing.
  const insertAtCursor = useCallback((snippet: string) => {
    const ta = textareaRef.current;
    setContent(prev => {
      if (!ta) return prev + snippet;
      const start = ta.selectionStart ?? prev.length;
      const end = ta.selectionEnd ?? prev.length;
      const next = prev.slice(0, start) + snippet + prev.slice(end);
      // Restore caret on the next tick, after React re-renders the value.
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + snippet.length;
        ta.setSelectionRange(pos, pos);
      });
      return next;
    });
  }, []);

  // Wrap the current selection with markdown markers (bold/italic/code/…).
  // With no selection, drops a placeholder between the markers and selects it.
  const surround = useCallback((left: string, right = left, placeholder = 'текст') => {
    const ta = textareaRef.current; if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    setContent(prev => {
      const sel = prev.slice(s, e) || placeholder;
      const next = prev.slice(0, s) + left + sel + right + prev.slice(e);
      requestAnimationFrame(() => {
        ta.focus();
        const a = s + left.length;
        ta.setSelectionRange(a, a + sel.length);
      });
      return next;
    });
  }, []);

  // Prefix each selected line (headings / lists / quotes). Strips any existing
  // block prefix first so toggling between H2/list/quote doesn't stack them.
  const linePrefix = useCallback((prefix: string) => {
    const ta = textareaRef.current; if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    setContent(prev => {
      const ls = prev.lastIndexOf('\n', s - 1) + 1;
      let le = prev.indexOf('\n', e); if (le === -1) le = prev.length;
      const block = prev.slice(ls, le);
      const newBlock = block.split('\n')
        .map(l => prefix + l.replace(/^(#{1,6}\s|>\s|[-*]\s|\d+\.\s)/, ''))
        .join('\n');
      const next = prev.slice(0, ls) + newBlock + prev.slice(le);
      requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(ls, ls + newBlock.length); });
      return next;
    });
  }, []);

  const insertLink = useCallback(() => {
    const ta = textareaRef.current; if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    setContent(prev => {
      const sel = prev.slice(s, e) || 'текст ссылки';
      const snippet = `[${sel}](https://)`;
      const next = prev.slice(0, s) + snippet + prev.slice(e);
      requestAnimationFrame(() => {
        ta.focus();
        const urlStart = s + sel.length + 3; // after "](":
        ta.setSelectionRange(urlStart, urlStart + 8); // select "https://"
      });
      return next;
    });
  }, []);

  const insertTable = useCallback(() => {
    insertAtCursor('\n| Колонка 1 | Колонка 2 |\n| --- | --- |\n| ячейка | ячейка |\n');
  }, [insertAtCursor]);

  const uploadAndInsert = useCallback(async (file: File) => {
    setUploadingImage(true);
    const toastId = toast.loading('Загрузка файла…');
    try {
      const res = await kbApi.uploadFile(file);
      const label = (file.name || 'файл').replace(/\.[a-z0-9]+$/i, '');
      // Images embed inline (![]()); everything else is a link ([]()) that
      // MarkdownView renders as a player (video/audio) or download chip (file).
      if (res.kind === 'image') {
        insertAtCursor(`\n![${label}](${res.url})\n`);
      } else {
        insertAtCursor(`\n[${file.name || label}](${res.url})\n`);
      }
      toast.success('Файл вставлен', { id: toastId });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Ошибка загрузки файла', { id: toastId });
    } finally {
      setUploadingImage(false);
    }
  }, [insertAtCursor]);

  // Ctrl+V со скриншотом/файлом в буфере — главный сценарий.
  const onPaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          uploadAndInsert(file);
          return;
        }
      }
    }
  }, [uploadAndInsert]);

  const onDrop = useCallback((e: React.DragEvent<HTMLTextAreaElement>) => {
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    e.preventDefault();
    uploadAndInsert(files[0]);
  }, [uploadAndInsert]);

  useEffect(() => {
    kbApi.taxonomy()
      .then((res) => setTaxonomy(res.categories))
      .catch(() => { /* suggestions are optional */ });
  }, []);

  useEffect(() => {
    if (isNew || !id) return;
    let alive = true;
    (async () => {
      try {
        const res = await kbApi.get(id);
        if (!alive) return;
        const a = res.article;
        setTitle(a.title);
        setSlug(a.slug);
        setExcerpt(a.excerpt || '');
        setContent(a.content);
        setCategory(a.category || '');
        setSubcategory(a.subcategory || '');
        setTagsInput((a.tags || []).join(', '));
        setStatus(a.status);
      } catch (e: any) {
        toast.error(e?.message || 'Не удалось загрузить статью');
        navigate('/kb');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, isNew, navigate]);

  if (!isAdmin) {
    return (
      <KBLayout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-foreground">Доступ запрещён</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Создавать и редактировать статьи могут только admin/superadmin.
          </p>
        </div>
      </KBLayout>
    );
  }
  if (loading) {
    return (
      <KBLayout>
        <div className="text-center py-12 text-muted-foreground">Загрузка…</div>
      </KBLayout>
    );
  }

  const parsedTags = tagsInput
    .split(/[,\n]/)
    .map(t => t.trim().replace(/^#/, ''))
    .filter(Boolean);

  const onSave = async (saveStatus?: KBStatus) => {
    const effectiveStatus = saveStatus ?? status;
    if (!title.trim() || title.trim().length < 3) {
      toast.error('Введите заголовок (мин. 3 символа)');
      return;
    }
    if (!content.trim()) {
      toast.error('Содержимое не может быть пустым');
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        slug: slug.trim() || undefined,
        excerpt: excerpt.trim() || undefined,
        content,
        category: category.trim() || undefined,
        subcategory: subcategory.trim() || undefined,
        tags: parsedTags,
        status: effectiveStatus,
      };
      const res = isNew ? await kbApi.create(body) : await kbApi.update(id!, body);
      toast.success(isNew ? 'Статья создана' : 'Статья сохранена');
      navigate(`/kb/${res.article.id}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <KBLayout>
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={isNew ? '/kb' : `/kb/${id}`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Назад
        </Link>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onSave('draft')}
            disabled={saving}
            className="h-9 px-3 rounded-md border border-border bg-card hover:bg-accent text-sm disabled:opacity-50"
          >
            Сохранить черновик
          </button>
          <button
            type="button"
            onClick={() => onSave('published')}
            disabled={saving}
            className="h-9 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm disabled:opacity-50"
          >
            {saving ? 'Сохранение…' : status === 'published' ? 'Сохранить' : 'Опубликовать'}
          </button>
        </div>
      </div>

      {/* Metadata row */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Заголовок статьи"
          className="w-full text-xl font-semibold bg-transparent border-b border-border focus:outline-none focus:border-primary py-2 text-foreground"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
          <Field label="Slug (опционально)">
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="auto"
              className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm"
            />
          </Field>
          <Field label="Категория">
            <input
              type="text"
              list="kb-cat-list"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Например: Скрипты ответов"
              className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm"
            />
            <datalist id="kb-cat-list">
              {taxonomy.map((c) => <option key={c.category} value={c.category} />)}
            </datalist>
          </Field>
          <Field label="Подкатегория">
            <input
              type="text"
              list="kb-sub-list"
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
              placeholder="Например: Активация Windows"
              className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm"
              disabled={!category.trim()}
              title={!category.trim() ? 'Сначала укажите категорию' : ''}
            />
            <datalist id="kb-sub-list">
              {(taxonomy.find((c) => c.category === category)?.subcategories || [])
                .map((s) => <option key={s.name} value={s.name} />)}
            </datalist>
          </Field>
          <Field label="Теги (через запятую)">
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="windows, office, активация"
              className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm"
            />
          </Field>
          <Field label="Статус">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as KBStatus)}
              className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm"
            >
              <option value="draft">Черновик</option>
              <option value="published">Опубликована</option>
              <option value="archived">Архив</option>
            </select>
          </Field>
        </div>
        <Field label="Краткое описание для списка (опционально)">
          <input
            type="text"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Если пусто — возьмём первые строки текста"
            className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm"
          />
        </Field>
      </div>

      {/* Editor / preview tabs */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="flex border-b border-border">
          <TabBtn active={tab === 'edit'} onClick={() => setTab('edit')}
                  icon={<PencilSquareIcon className="h-4 w-4" />} label="Редактирование" />
          <TabBtn active={tab === 'preview'} onClick={() => setTab('preview')}
                  icon={<EyeIcon className="h-4 w-4" />} label="Предпросмотр" />
          {/* File/media upload — images, video, audio, docs. Also reachable
              via Ctrl+V / drag&drop right in the textarea. */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImage}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            title="Вставить файл: фото, видео, аудио, документ (или Ctrl+V / перетащить в текст)"
          >
            <PaperClipIcon className="h-4 w-4" />
            {uploadingImage ? 'Загрузка…' : 'Файл / медиа'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/mp4,video/webm,video/quicktime,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.7z,.tar,.gz,.tgz,.bz2,.xz,.cab,.cer,.crt,.der,.pem,.pfx,.p12,.p7b,.p7c,.crl,.csr,.key,.auth,.esl,.bat,.cmd,.ps1,.sh,.py,.reg,.vbs,.ahk,.ini,.cfg,.conf,.json,.yml,.yaml,.md,.csv,.log,.sql,.xml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadAndInsert(f);
              e.target.value = '';
            }}
          />
          <span className="ml-auto px-3 py-2 text-[11px] text-muted-foreground self-center hidden sm:block">
            Markdown · GFM · Ctrl+V вставляет скриншот
          </span>
        </div>
        {tab === 'edit' ? (
          <>
          {/* Панель форматирования — кнопки оборачивают выделение в markdown,
              чтобы оформлять статью без знания синтаксиса. */}
          <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-muted/30">
            <FmtBtn label="H1" title="Заголовок 1" onClick={() => linePrefix('# ')} />
            <FmtBtn label="H2" title="Заголовок 2" onClick={() => linePrefix('## ')} />
            <FmtBtn label="H3" title="Заголовок 3" onClick={() => linePrefix('### ')} />
            <Sep />
            <FmtBtn label="Ж" bold title="Жирный (Ctrl+B)" onClick={() => surround('**')} />
            <FmtBtn label="К" italic title="Курсив (Ctrl+I)" onClick={() => surround('*')} />
            <FmtBtn label="S" strike title="Зачёркнутый" onClick={() => surround('~~')} />
            <FmtBtn label="</>" mono title="Моноширинный код" onClick={() => surround('`')} />
            <Sep />
            <FmtBtn label="•" title="Маркированный список" onClick={() => linePrefix('- ')} />
            <FmtBtn label="1." title="Нумерованный список" onClick={() => linePrefix('1. ')} />
            <FmtBtn label="❝" title="Цитата" onClick={() => linePrefix('> ')} />
            <FmtBtn label="{ }" title="Блок кода" onClick={() => surround('\n```\n', '\n```\n', 'код')} />
            <Sep />
            <FmtBtn label="🔗" title="Ссылка" onClick={insertLink} />
            <FmtBtn label="▦" title="Таблица" onClick={insertTable} />
            <FmtBtn label="—" title="Разделитель" onClick={() => insertAtCursor('\n\n---\n\n')} />
          </div>
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onPaste={onPaste}
            onDrop={onDrop}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
                const k = e.key.toLowerCase();
                if (k === 'b') { e.preventDefault(); surround('**'); }
                else if (k === 'i') { e.preventDefault(); surround('*'); }
                else if (k === 'k') { e.preventDefault(); insertLink(); }
              }
            }}
            placeholder={'# Заголовок\n\nТекст статьи с поддержкой **markdown**…\n\nВыделите текст и нажмите кнопку на панели выше, либо Ctrl+B / Ctrl+I.\nСкриншот/файл: Ctrl+V или перетащите прямо сюда.'}
            className="w-full min-h-[60vh] p-4 bg-background font-mono text-sm leading-relaxed text-foreground focus:outline-none"
          />
          </>
        ) : (
          <div className="p-5 min-h-[60vh]">
            {content.trim()
              ? <MarkdownView source={content} />
              : <p className="text-sm text-muted-foreground italic">Здесь появится предпросмотр…</p>}
          </div>
        )}
      </div>
    </div>
    </KBLayout>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</span>
    {children}
  </label>
);

const FmtBtn: React.FC<{
  label: string; title: string; onClick: () => void;
  bold?: boolean; italic?: boolean; strike?: boolean; mono?: boolean;
}> = ({ label, title, onClick, bold, italic, strike, mono }) => (
  <button
    type="button"
    title={title}
    // onMouseDown (not click) + preventDefault keeps the textarea selection
    // intact — a normal click would blur the textarea and lose it first.
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={`min-w-[30px] h-7 px-2 rounded text-sm text-foreground/80 hover:bg-accent hover:text-foreground transition-colors ${
      bold ? 'font-bold' : ''} ${italic ? 'italic' : ''} ${strike ? 'line-through' : ''} ${mono ? 'font-mono text-xs' : ''}`}
  >
    {label}
  </button>
);

const Sep: React.FC = () => <span className="mx-1 w-px h-5 bg-border" />;

const TabBtn: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }>
  = ({ active, onClick, icon, label }) => (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors ${
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {icon}{label}
    </button>
  );

export default KBArticleEditPage;
